/* Pilot feedback widget for the Emporium soft launch. Config comes from snippets/pilot-feedback.liquid. */
(() => {
  'use strict';
  const cfg = window.PilotFeedback;
  if (!cfg || !cfg.endpoint) return;

  const store = {
    get: (k) => { try { return localStorage.getItem(k); } catch (_) { return null; } },
    set: (k, v) => { try { localStorage.setItem(k, v); } catch (_) {} },
    del: (k) => { try { localStorage.removeItem(k); } catch (_) {} },
  };

  // ---- Who sees it ----
  const pilotParam = new URLSearchParams(location.search).get('pilot');
  if (pilotParam && pilotParam === cfg.invite) store.set('pf_on', '1');
  if (pilotParam === 'off') store.del('pf_on');
  if (!cfg.tagged && store.get('pf_on') !== '1') return;

  // ---- Error buffer (inline snippet starts it; we add console.error) ----
  const errors = window.__pfErrors || [];
  const push = window.__pfPush || (() => {});
  const origError = console.error;
  console.error = function (...args) {
    try {
      push('console', args.map((a) => (a instanceof Error ? a.stack || a.message : typeof a === 'object' ? JSON.stringify(a) : String(a))).join(' '));
    } catch (_) {}
    return origError.apply(this, args);
  };

  // modern-screenshot lets the browser render the page (SVG foreignObject), so theme CSS comes out right.
  const SHOT_URL = 'https://cdn.jsdelivr.net/npm/modern-screenshot@4.7.0/dist/index.js';
  const LOGICAL_BOX = ['inset-block', 'inset-inline', 'inset-block-start', 'inset-block-end', 'inset-inline-start', 'inset-inline-end', 'block-size', 'inline-size'];
  const BLANK_IMG ='data:image/gif;base64,R0lGODlhAQABAAAAACH5BAEKAAEALAAAAAABAAEAAAICTAEAOw==';
  const MAX_W = 1280;
  const SEVERITIES = [
    ['blocker', 'Blocks me'],
    ['major', 'Annoying'],
    ['minor', 'Looks off'],
    ['idea', 'Idea'],
  ];

  // ---- Styles ----
  const css = `
#pf-root{--pf-ink:#000;--pf-paper:#fff;--pf-line:#d6d6d6;--pf-mute:#6b6b6b;--pf-mark:#d0021b;font:inherit;color:var(--pf-ink)}
#pf-root *{box-sizing:border-box}
#pf-root [hidden]{display:none!important}
#pf-root .pf-fab{position:fixed;right:16px;bottom:calc(16px + env(safe-area-inset-bottom,0px));z-index:2147483000;background:var(--pf-ink);color:var(--pf-paper);border:0;border-radius:999px;padding:10px 18px;font:inherit;font-size:14px;letter-spacing:.02em;cursor:pointer;box-shadow:0 2px 10px rgba(0,0,0,.18)}
#pf-root .pf-fab:disabled{opacity:.7;cursor:progress}
#pf-root .pf-scrim{position:fixed;inset:0;background:rgba(0,0,0,.35);z-index:2147483001}
#pf-root .pf-sheet{position:fixed;left:0;right:0;bottom:0;z-index:2147483002;background:var(--pf-paper);border-radius:14px 14px 0 0;max-height:92vh;overflow:auto;padding:18px 18px calc(18px + env(safe-area-inset-bottom,0px))}
@media (min-width:720px){#pf-root .pf-sheet{left:auto;right:24px;bottom:24px;width:440px;border-radius:12px;border:1px solid var(--pf-line)}}
@media (prefers-reduced-motion:no-preference){#pf-root .pf-sheet{animation:pf-up .22s ease-out}@keyframes pf-up{from{transform:translateY(24px);opacity:0}to{transform:none;opacity:1}}}
#pf-root .pf-head{display:flex;align-items:center;justify-content:space-between;margin-bottom:12px}
#pf-root h2{margin:0;font:inherit;font-size:18px;font-weight:600}
#pf-root .pf-x{background:none;border:0;font-size:26px;line-height:1;padding:4px 8px;cursor:pointer;color:var(--pf-ink)}
#pf-root .pf-canvas{display:block;width:100%;max-height:42vh;object-fit:contain;border:1px solid var(--pf-line);border-radius:6px;touch-action:none;cursor:crosshair;background:#f3f3f3}
#pf-root .pf-empty{margin:0;padding:22px 12px;text-align:center;border:1px dashed var(--pf-line);border-radius:6px;color:var(--pf-mute);font-size:14px}
#pf-root .pf-tools{display:flex;flex-wrap:wrap;gap:6px 14px;align-items:center;margin:8px 0 16px;font-size:13px;color:var(--pf-mute)}
#pf-root .pf-link{background:none;border:0;padding:0;font:inherit;font-size:13px;color:var(--pf-ink);text-decoration:underline;cursor:pointer}
#pf-root .pf-field{display:block;margin-bottom:14px}
#pf-root .pf-field span,#pf-root legend{display:block;font-size:14px;font-weight:600;margin-bottom:6px;padding:0}
#pf-root textarea,#pf-root input[type=text]{width:100%;font:inherit;font-size:16px;padding:10px;border:1px solid var(--pf-line);border-radius:6px;background:var(--pf-paper);color:var(--pf-ink)}
#pf-root fieldset{border:0;margin:0 0 14px;padding:0}
#pf-root .pf-sev{display:flex;flex-wrap:wrap;gap:8px}
#pf-root .pf-sev label{position:relative}
#pf-root .pf-sev input{position:absolute;opacity:0;inset:0}
#pf-root .pf-sev span{display:inline-block;padding:8px 14px;border:1px solid var(--pf-line);border-radius:999px;font-size:14px;cursor:pointer}
#pf-root .pf-sev input:checked+span{background:var(--pf-ink);color:var(--pf-paper);border-color:var(--pf-ink)}
#pf-root .pf-sev input:focus-visible+span,#pf-root button:focus-visible,#pf-root textarea:focus-visible,#pf-root input:focus-visible{outline:2px solid var(--pf-mark);outline-offset:2px}
#pf-root .pf-status{min-height:1.2em;margin:0 0 10px;font-size:14px}
#pf-root .pf-status.is-error{color:var(--pf-mark)}
#pf-root .pf-actions{display:flex;justify-content:space-between;align-items:center;gap:12px}
#pf-root .pf-send{margin-left:auto;background:var(--pf-ink);color:var(--pf-paper);border:0;border-radius:6px;padding:12px 20px;font:inherit;font-size:15px;cursor:pointer}
#pf-root .pf-send:disabled{opacity:.6;cursor:progress}`;

  // ---- Markup ----
  const root = document.createElement('div');
  root.id = 'pf-root';
  root.innerHTML = `
<button class="pf-fab" type="button" aria-haspopup="dialog">Feedback</button>
<div class="pf-scrim" hidden></div>
<section class="pf-sheet" role="dialog" aria-modal="true" aria-labelledby="pf-title" hidden>
  <div class="pf-head"><h2 id="pf-title">Send feedback</h2><button class="pf-x" type="button" aria-label="Close">&times;</button></div>
  <canvas class="pf-canvas" role="img" aria-label="Screenshot of this page. Draw on it to mark the problem."></canvas>
  <p class="pf-empty pf-shooting" hidden>Taking a screenshot of the page…</p>
  <p class="pf-empty pf-failed" hidden>The screenshot didn't work on this page. Add your own below.</p>
  <div class="pf-tools">
    <span class="pf-hint">Draw on the screenshot to mark the problem.</span>
    <button type="button" class="pf-link pf-clear">Clear marks</button>
    <button type="button" class="pf-link pf-own">Use my own screenshot</button>
    <input type="file" accept="image/*" hidden>
  </div>
  <label class="pf-field"><span>What happened?</span>
    <textarea rows="3" placeholder="For example: the size list doesn't open on this jacket"></textarea></label>
  <fieldset><legend>How bad is it?</legend><div class="pf-sev">
    ${SEVERITIES.map(([v, l], i) => `<label><input type="radio" name="pf-sev" value="${v}"${i === 1 ? ' checked' : ''}><span>${l}</span></label>`).join('')}
  </div></fieldset>
  <label class="pf-field pf-name" hidden><span>Your name</span><input type="text" autocomplete="name"></label>
  <p class="pf-status" role="status" aria-live="polite"></p>
  <div class="pf-actions">
    <button type="button" class="pf-link pf-shake" hidden>Turn on shake to report</button>
    <button type="button" class="pf-send">Send feedback</button>
  </div>
</section>`;

  const style = document.createElement('style');
  style.textContent = css;
  document.head.appendChild(style);
  document.body.appendChild(root);

  const $ = (s) => root.querySelector(s);
  const fab = $('.pf-fab'), scrim = $('.pf-scrim'), sheet = $('.pf-sheet');
  const canvas = $('.pf-canvas'), ctx = canvas.getContext('2d');
  const empty = $('.pf-failed'), shooting = $('.pf-shooting'), fileInput = $('input[type=file]');
  const text = $('textarea'), nameWrap = $('.pf-name'), nameInput = $('.pf-name input');
  const statusEl = $('.pf-status'), sendBtn = $('.pf-send'), shakeBtn = $('.pf-shake');

  let isOpen = false, hasShot = false, drawing = false;
  // shotSeq lets a newer screenshot (their own file, or a later capture) win over a slower one still running.
  let shotSeq = 0, shotPromise = null;
  const base = document.createElement('canvas'); // unmarked copy for "Clear marks"

  const setStatus = (msg, isError) => {
    statusEl.textContent = msg || '';
    statusEl.classList.toggle('is-error', !!isError);
  };

  // ---- Screenshot ----
  let scriptLoad = null; // shared by the idle preload and the first capture
  const loadScript = (src) => {
    if (window.modernScreenshot) return Promise.resolve();
    if (!scriptLoad) {
      scriptLoad = new Promise((res, rej) => {
        const s = document.createElement('script');
        s.src = src; s.onload = res;
        s.onerror = () => { scriptLoad = null; s.remove(); rej(new Error('load failed')); };
        document.head.appendChild(s);
      });
    }
    return scriptLoad;
  };
  const withTimeout = (p, ms) => Promise.race([p, new Promise((_, rej) => setTimeout(() => rej(new Error('timeout')), ms))]);

  function setImage(src, w, h) {
    const scale = Math.min(1, MAX_W / w);
    canvas.width = base.width = Math.round(w * scale);
    canvas.height = base.height = Math.round(h * scale);
    ctx.drawImage(src, 0, 0, canvas.width, canvas.height);
    base.getContext('2d').drawImage(canvas, 0, 0);
    hasShot = true;
    canvas.hidden = false; empty.hidden = true; shooting.hidden = true;
  }

  function noShot() {
    hasShot = false;
    canvas.hidden = true; empty.hidden = false; shooting.hidden = true;
  }

  // Only what's on screen is copied: hidden elements and everything below the fold are skipped,
  // off-screen images become a 1px placeholder, and fixed/sticky bars are pinned where they are now.
  function prepareViewport() {
    const vw = window.innerWidth, vh = window.innerHeight, sx = window.scrollX, sy = window.scrollY;
    const drop = new WeakSet();
    const pinned = [];
    const pulled = [];
    // Extra CSS for particular elements' copies, applied after the library's own styles.
    const patches = new Map();
    const patch = (el, css) => patches.set(el, (patches.get(el) || '') + ';' + css);
    for (const el of document.body.getElementsByTagName('*')) {
      if (el.id === 'pf-root') { drop.add(el); continue; }
      const cs = getComputedStyle(el);
      if (cs.display === 'none') { drop.add(el); continue; }
      const r = el.getBoundingClientRect();
      if (cs.position === 'fixed' || cs.position === 'sticky') {
        if (r.bottom > 0 && r.top < vh) pinned.push([el, cs.position, r]);
      } else if (r.top > vh + 50) {
        drop.add(el);
        continue;
      }
      // (absolutely positioned ones, like the -1px screen-reader labels, don't collapse)
      if (parseFloat(cs.marginBottom) < -1 && cs.position !== 'absolute' && cs.position !== 'fixed') pulled.push([el, r]);
    }
    // The theme lays the header over the first banner with a negative bottom margin that collapses
    // out through its wrappers. The library copies every element's measured height, and a fixed height
    // stops that collapse, which pushed the page down under a gap. Wrappers it passes through keep auto.
    pulled.forEach(([el, r]) => {
      for (let p = el.parentElement; p && p !== document.body; p = p.parentElement) {
        const ps = getComputedStyle(p);
        if (ps.display !== 'block' || ps.overflow !== 'visible' || parseFloat(ps.paddingBottom) || parseFloat(ps.borderBottomWidth)) break;
        if (Math.abs(p.getBoundingClientRect().bottom - r.bottom) > 1) break;
        patch(p, 'height:auto');
      }
    });
    const offscreen = new Set();
    // The library first waits for every <img> and <video> on the page to load. Lazy ones further down
    // never do, so it sat out its whole timeout (5s). Report unfinished ones as ready for the duration
    // of the capture; visible images are downloaded separately by imageFetcher anyway.
    const stalled = [];
    const stall = (el, props) => {
      Object.entries(props).forEach(([k, v]) => Object.defineProperty(el, k, { value: v, configurable: true }));
      stalled.push([el, Object.keys(props)]);
    };
    for (const img of document.images) {
      const r = img.getBoundingClientRect();
      const seen = r.width && r.height && r.bottom > 0 && r.top < vh && r.right > 0 && r.left < vw;
      if (!seen && (img.currentSrc || img.src)) offscreen.add(img.currentSrc || img.src);
      if (!img.complete) stall(img, { complete: true, decode: () => Promise.resolve() });
    }
    for (const v of document.getElementsByTagName('video')) {
      if (v.readyState < 2) stall(v, { readyState: 4 });
    }
    // The clone is shifted up/left by the scroll offset, so pinned bars get their current spot added back.
    // Sticky ones are measured against where they'd sit unstuck and nudged by the difference.
    pinned.forEach(([el, pos, r]) => {
      if (pos === 'fixed') {
        patch(el, `top:${sy + r.top}px;left:${sx + r.left}px;bottom:auto;right:auto;width:${r.width}px`);
      } else {
        // static, not relative: the theme's own `top` (e.g. 40px under the announcement bar) would skew it
        const prev = el.style.position;
        el.style.position = 'static';
        const s = el.getBoundingClientRect();
        el.style.position = prev;
        patch(el, `position:relative;top:${r.top - s.top}px;left:${r.left - s.left}px`);
      }
    });
    // The copy is found again by a temporary attribute, which cloning carries over.
    const tagged = [...patches.keys()];
    tagged.forEach((el, i) => el.setAttribute('data-pf-fix', String(i)));
    return {
      vw, vh, sx, sy, drop, offscreen,
      fixes: tagged.map((el) => patches.get(el)),
      cleanup: () => {
        tagged.forEach((el) => el.removeAttribute('data-pf-fix'));
        stalled.forEach(([el, keys]) => keys.forEach((k) => { delete el[k]; }));
      },
    };
  }

  // Visible images are fetched here, reusing one download per URL.
  const toDataUrl = (blob) => new Promise((res, rej) => {
    const fr = new FileReader();
    fr.onload = () => res(fr.result); fr.onerror = rej;
    fr.readAsDataURL(blob);
  });
  function imageFetcher(offscreen) {
    const cache = new Map();
    return (url) => {
      if (offscreen.has(url)) return Promise.resolve(BLANK_IMG);
      if (/\.(woff2?|ttf|otf)(\?|$)/i.test(url)) return Promise.resolve(false);
      if (!cache.has(url)) {
        cache.set(url, withTimeout(
          fetch(url, { mode: 'cors', credentials: 'omit' }).then((r) => (r.ok ? r.blob() : Promise.reject(r.status))).then(toDataUrl),
          5000,
        ).catch(() => false));
      }
      return cache.get(url);
    };
  }

  // Inside the rendered SVG the browser would pick a srcset / <picture><source> candidate, which it
  // isn't allowed to load there, instead of the embedded src, so images came out black.
  function stripResponsive(c) {
    if (c.nodeType === 1 && c.tagName === 'IMG') {
      c.removeAttribute('srcset'); c.removeAttribute('sizes'); c.removeAttribute('loading');
    }
  }

  // Runs while the panel is already open. The panel itself is skipped (#pf-root is in `drop`), and the
  // page is measured first, synchronously, before anything else can change it.
  async function capture() {
    const seq = ++shotSeq;
    hasShot = false;
    canvas.hidden = true; empty.hidden = true; shooting.hidden = false;
    const view = prepareViewport();
    try {
      await withTimeout(loadScript(SHOT_URL), 6000);
      const fetchImage = imageFetcher(view.offscreen);
      const bg = getComputedStyle(document.body).backgroundColor;
      const shot = await withTimeout(window.modernScreenshot.domToCanvas(document.documentElement, {
        width: view.vw,
        height: view.vh,
        scale: Math.min(window.devicePixelRatio || 1, 1.5),
        backgroundColor: bg && bg !== 'rgba(0, 0, 0, 0)' ? bg : '#fff',
        timeout: 5000,
        filter: (n) => !(n.nodeType === 1 && (view.drop.has(n) || (n.tagName === 'SOURCE' && n.parentNode && n.parentNode.tagName === 'PICTURE'))),
        fetchFn: fetchImage,
        style: { transform: `translate(${-view.sx}px, ${-view.sy}px)` },
        onCloneEachNode: (c) => {
          stripResponsive(c);
          const i = c.nodeType === 1 && c.getAttribute('data-pf-fix');
          if (i) {
            // The library also copies the logical twins (inset-block: 0px, block-size…), which serialise
            // after `top`/`height` and silently put them back. Drop them before applying the fix.
            LOGICAL_BOX.forEach((p) => c.style.removeProperty(p));
            c.style.cssText += ';' + view.fixes[i];
            c.removeAttribute('data-pf-fix');
          }
        },
      }), 10000);
      if (seq === shotSeq) setImage(shot, shot.width, shot.height);
    } catch (e) {
      if (seq === shotSeq) noShot();
    } finally {
      view.cleanup();
    }
  }

  // Fetch the library once the page is idle, so tapping Feedback doesn't wait for the download.
  const preload = () => loadScript(SHOT_URL).catch(() => {});
  if (document.readyState === 'complete') setTimeout(preload, 2000);
  else window.addEventListener('load', () => setTimeout(preload, 2000), { once: true });

  fileInput.addEventListener('change', () => {
    const f = fileInput.files && fileInput.files[0];
    if (!f) return;
    shotSeq++; // a capture still running must not replace their own screenshot
    const img = new Image();
    const url = URL.createObjectURL(f);
    img.onload = () => { setImage(img, img.naturalWidth, img.naturalHeight); URL.revokeObjectURL(url); };
    img.onerror = () => { setStatus("That file couldn't be opened. Try a PNG or JPG screenshot.", true); URL.revokeObjectURL(url); };
    img.src = url;
    fileInput.value = '';
  });
  $('.pf-own').addEventListener('click', () => fileInput.click());
  $('.pf-clear').addEventListener('click', () => { if (hasShot) ctx.drawImage(base, 0, 0); });

  // ---- Drawing ----
  const pos = (e) => {
    const r = canvas.getBoundingClientRect();
    return { x: (e.clientX - r.left) * (canvas.width / r.width), y: (e.clientY - r.top) * (canvas.height / r.height) };
  };
  canvas.addEventListener('pointerdown', (e) => {
    if (!hasShot) return;
    drawing = true;
    canvas.setPointerCapture(e.pointerId);
    const p = pos(e);
    ctx.strokeStyle = '#d0021b';
    ctx.lineWidth = Math.max(4, canvas.width / 180);
    ctx.lineCap = ctx.lineJoin = 'round';
    ctx.beginPath(); ctx.moveTo(p.x, p.y);
  });
  canvas.addEventListener('pointermove', (e) => {
    if (!drawing) return;
    const p = pos(e);
    ctx.lineTo(p.x, p.y); ctx.stroke();
  });
  ['pointerup', 'pointercancel', 'pointerleave'].forEach((t) => canvas.addEventListener(t, () => { drawing = false; }));

  // ---- Open / close ----
  // The panel opens at once and the screenshot fills in while they type. No autofocus until it's done:
  // on phones the keyboard would resize the page mid-capture.
  function open() {
    if (isOpen) return;
    isOpen = true;
    shotPromise = capture();
    fab.hidden = true;
    nameWrap.hidden = !!(cfg.tester && cfg.tester.name);
    if (!nameWrap.hidden) nameInput.value = store.get('pf_name') || '';
    setStatus('');
    scrim.hidden = false; sheet.hidden = false;
    shotPromise.then(() => { if (isOpen && !root.contains(document.activeElement)) text.focus(); });
  }
  function close() {
    isOpen = false;
    scrim.hidden = true; sheet.hidden = true;
    fab.hidden = false; fab.focus();
  }
  fab.addEventListener('click', open);
  $('.pf-x').addEventListener('click', close);
  scrim.addEventListener('click', close);
  document.addEventListener('keydown', (e) => { if (e.key === 'Escape' && isOpen) close(); });

  // ---- Shake to report ----
  let hits = 0, hitStart = 0, cooldown = 0;
  function onMotion(e) {
    const a = e.accelerationIncludingGravity;
    if (!a || a.x == null) return;
    const now = Date.now();
    if (Math.hypot(a.x, a.y, a.z) > 22) {
      if (now - hitStart > 800) { hits = 0; hitStart = now; }
      hits += 1;
      if (hits >= 3 && now > cooldown && !isOpen) { cooldown = now + 3000; hits = 0; open(); }
    }
  }
  const enableShake = () => window.addEventListener('devicemotion', onMotion);
  if ('DeviceMotionEvent' in window) {
    if (typeof DeviceMotionEvent.requestPermission === 'function') {
      shakeBtn.hidden = false; // iOS: needs a tap to allow motion access
      shakeBtn.addEventListener('click', async () => {
        try {
          const res = await DeviceMotionEvent.requestPermission();
          if (res === 'granted') { enableShake(); shakeBtn.hidden = true; setStatus('Shake to report is on for this visit.'); }
          else setStatus('Motion access was blocked. Use the Feedback button instead.', true);
        } catch (_) { setStatus('Motion access was blocked. Use the Feedback button instead.', true); }
      });
    } else {
      enableShake();
    }
  }

  // ---- Send ----
  async function getCart() {
    try {
      const r = await fetch('/cart.js', { credentials: 'same-origin' });
      const c = await r.json();
      return {
        currency: c.currency,
        total: c.total_price / 100,
        items: (c.items || []).map((i) => ({
          title: i.product_title, variant: i.variant_title, variant_id: i.variant_id, qty: i.quantity, line: i.final_line_price / 100,
        })),
      };
    } catch (_) { return null; }
  }

  sendBtn.addEventListener('click', async () => {
    const description = text.value.trim();
    if (!description) { setStatus('Describe what happened before sending.', true); text.focus(); return; }

    let tester = cfg.tester;
    if (!tester || !tester.name) {
      const n = nameInput.value.trim();
      if (!n) { setStatus('Add your name so we can follow up.', true); nameInput.focus(); return; }
      store.set('pf_name', n);
      tester = { name: n, email: null };
    }

    const severity = (root.querySelector('input[name=pf-sev]:checked') || {}).value || 'major';
    const context = JSON.parse(JSON.stringify(cfg.context || {}));
    const urlVariant = new URLSearchParams(location.search).get('variant');
    if (context.product && urlVariant) context.product.variant = Number(urlVariant) || urlVariant;

    sendBtn.disabled = true;
    if (shotPromise) {
      setStatus('Finishing the screenshot…');
      await shotPromise;
    }

    let screenshot = null, screenshotNote = null;
    if (hasShot) {
      try { screenshot = canvas.toDataURL('image/jpeg', 0.72); }
      catch (_) { screenshotNote = 'Screenshot blocked by browser (cross-origin image).'; }
    }

    setStatus('Sending…');
    const payload = {
      description, severity, tester, context,
      page: { url: location.href, path: location.pathname, title: document.title, referrer: document.referrer || null },
      env: {
        userAgent: navigator.userAgent,
        viewport: `${window.innerWidth}x${window.innerHeight}`,
        screen: `${screen.width}x${screen.height}`,
        pixelRatio: window.devicePixelRatio,
        language: navigator.language,
        timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
        online: navigator.onLine,
        time: new Date().toISOString(),
      },
      errors: errors.slice(-15),
      cart: await getCart(),
      screenshot, screenshotNote,
    };

    try {
      const res = await fetch(cfg.endpoint, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'X-Pilot-Key': cfg.invite },
        body: JSON.stringify(payload),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(data.error || `HTTP ${res.status}`);
      setStatus(`Sent. Reference ${data.key}.`);
      text.value = '';
      setTimeout(() => { if (isOpen) close(); setStatus(''); }, 1800);
    } catch (e) {
      setStatus("Couldn't send. Check your connection and try again. Your text is still here.", true);
    } finally {
      sendBtn.disabled = false;
    }
  });
})();
